import { refreshAuthTokens } from "@src/app/api/config/authRefresh";
import { getApiBaseUrl } from "@src/app/api/config/getApiBaseUrl";
import { clearClientAuthState } from "@src/domains/shared/services/authStorage";
import { getCsrfToken, setCsrfToken } from "@src/app/api/config/csrf";
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";

const baseUrl = getApiBaseUrl();

export interface ApiRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  skipAuthRefresh?: boolean;
}

export const api: AxiosInstance = axios.create({
  baseURL: baseUrl,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

const csrfClient: AxiosInstance = axios.create({
  baseURL: baseUrl,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

interface ICsrfTokenResponse {
  csrfToken: string;
}

let csrfBootstrapPromise: Promise<string | null> | null = null;
let refreshInFlight: Promise<boolean> | null = null;

const shouldBypassCsrfBootstrap = (requestUrl: string): boolean =>
  requestUrl.includes("/security/csrf-token");

const shouldSkipUnauthorizedRetry = (requestUrl: string): boolean =>
  requestUrl.includes("/auth/login") ||
  requestUrl.includes("/auth/refresh-token") ||
  requestUrl.includes("/auth/logout");

const refreshAccessToken = async (): Promise<boolean> => {
  if (!refreshInFlight) {
    refreshInFlight = refreshAuthTokens().finally(() => {
      refreshInFlight = null;
    });
  }

  return refreshInFlight;
};

const ensureCsrfToken = async (): Promise<string | null> => {
  const currentToken = getCsrfToken();
  if (currentToken) {
    return currentToken;
  }

  if (!csrfBootstrapPromise) {
    csrfBootstrapPromise = csrfClient
      .get<ICsrfTokenResponse>("/security/csrf-token")
      .then((response) => {
        const token = response.data.csrfToken ?? null;
        setCsrfToken(token);
        return token;
      })
      .catch(() => null)
      .finally(() => {
        csrfBootstrapPromise = null;
      });
  }

  return csrfBootstrapPromise;
};

api.interceptors.request.use(
  async (request) => {
    if (!request?.url) {
      throw new Error("No uri provided");
    }

    const method = request.method?.toUpperCase();
    if (
      method &&
      ["POST", "PUT", "PATCH", "DELETE"].includes(method)
    ) {
      const requestUrl = request.url;
      const csrfToken = shouldBypassCsrfBootstrap(requestUrl)
        ? getCsrfToken()
        : await ensureCsrfToken();
      if (csrfToken) {
        request.headers["X-CSRF-TOKEN"] = csrfToken;
      }
    }

    return request;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as ApiRequestConfig | undefined;
    const requestUrl = originalRequest?.url ?? "";

    if (
      error.response?.status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      originalRequest.skipAuthRefresh ||
      shouldSkipUnauthorizedRetry(requestUrl)
    ) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      await refreshAccessToken();
      return api(originalRequest);
    } catch {
      clearClientAuthState();
      return Promise.reject(error);
    }
  },
);

export default api;
